// client/src/pages/Agreement.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Modal,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Checkbox,
    Alert,
    Snackbar,
    IconButton,
    Divider,
    Chip,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Description as DescriptionIcon,
    Check as CheckIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { agreementApi } from '../api/agreementApi';
import ContractTermsModal from '../components/ContractTermsModal';

const Agreement = ({ affiliateId: propAffiliateId }) => {
    const location = useLocation();

    const [token] = useState(localStorage.getItem('token') || '');
    const [isAdmin, setIsAdmin] = useState(false);

    const [agreements, setAgreements] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [selectedAgreement, setSelectedAgreement] = useState(null);
    const [loading, setLoading] = useState(false);

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        affiliateId: '',
        content: ''
    });

    // Dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [agreementToDelete, setAgreementToDelete] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [acceptCheckbox, setAcceptCheckbox] = useState(false);

    // Contract Terms Modal
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [selectedContractTermsId, setSelectedContractTermsId] = useState(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Proovime esmalt laadida kõiki lepinguid (kui kasutaja on admin)
            try {
                const [agreementsData, affiliatesData] = await Promise.all([
                    agreementApi.getAgreements(token),
                    agreementApi.getAffiliates(token)
                ]);

                // Kui õnnestus, siis kasutaja ON admin
                setIsAdmin(true);
                setAgreements(agreementsData);
                setAffiliates(affiliatesData);
                return;
            } catch (adminError) {
                // Kui ebaõnnestus 403 viga, siis pole admin
                // Jätkame affiliate owner vaatega
                if (adminError.message.includes('Unauthorized') || adminError.message.includes('403')) {
                    setIsAdmin(false);
                } else {
                    throw adminError;
                }
            }

            // Kui kasutaja pole admin, proovi laadida affiliate lepingut
            const affiliateId = propAffiliateId || location.state?.affiliateId;
            if (affiliateId) {
                const agreement = await agreementApi.getAgreementByAffiliate(token, affiliateId);
                setAgreements(agreement ? [agreement] : []);
            } else {
                setAgreements([]);
            }
        } catch (error) {
            console.error('Error loading data:', error);
            showSnackbar('Failed to load data: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (agreement = null) => {
        if (agreement) {
            setEditMode(true);
            setFormData({
                affiliateId: agreement.affiliateId,
                content: agreement.content
            });
            setSelectedAgreement(agreement);
        } else {
            setEditMode(false);
            setFormData({
                affiliateId: '',
                content: ''
            });
            setSelectedAgreement(null);
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setFormData({
            affiliateId: '',
            content: ''
        });
        setSelectedAgreement(null);
        setEditMode(false);
    };

    const handleSave = async () => {
        if (!formData.affiliateId || !formData.content.trim()) {
            showSnackbar('Please fill in all fields', 'error');
            return;
        }

        setLoading(true);
        try {
            if (editMode && selectedAgreement) {
                await agreementApi.updateAgreement(token, selectedAgreement.id, {
                    content: formData.content
                });
                showSnackbar('Agreement updated successfully', 'success');
            } else {
                await agreementApi.createAgreement(token, formData);
                showSnackbar('Agreement created successfully', 'success');
            }
            handleCloseModal();
            loadData();
        } catch (error) {
            showSnackbar('Failed to save agreement: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (agreement) => {
        setAgreementToDelete(agreement);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!agreementToDelete) return;

        setLoading(true);
        try {
            await agreementApi.deleteAgreement(token, agreementToDelete.id);
            showSnackbar('Agreement deleted successfully', 'success');
            setDeleteDialogOpen(false);
            setAgreementToDelete(null);
            loadData();
        } catch (error) {
            showSnackbar('Failed to delete agreement: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewAgreement = (agreement) => {
        setSelectedAgreement(agreement);
        setAcceptCheckbox(false);
        setViewDialogOpen(true);
    };

    const handleAcceptAgreement = async () => {
        if (!selectedAgreement) return;

        setLoading(true);
        try {
            await agreementApi.acceptAgreement(token, selectedAgreement.id);
            showSnackbar('Agreement accepted successfully', 'success');
            setViewDialogOpen(false);
            setSelectedAgreement(null);
            setAcceptCheckbox(false);
            loadData();
        } catch (error) {
            showSnackbar('Failed to accept agreement: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const openTerms = () => {
        setTermsModalOpen(true);
        setSelectedContractTermsId('contract'); // Kasuta olemasolevat 'contract' tüüpi
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading && agreements.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {isAdmin ? 'Agreement Management' : 'My Agreement'}
                    </Typography>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal()}
                            disabled={loading}
                        >
                            Add Agreement
                        </Button>
                    )}
                </Box>

                {agreements.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="h6" color="text.secondary">
                            {isAdmin ? 'No agreements found. Create your first agreement!' : 'No agreement available for your affiliate yet.'}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {agreements.map((agreement) => (
                            <Grid item xs={12} md={6} lg={4} key={agreement.id}>
                                <Card
                                    elevation={2}
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 6
                                        }
                                    }}
                                    onClick={() => handleViewAgreement(agreement)}
                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {agreement.affiliate.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Agreement #{agreement.id}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Status:
                                            </Typography>
                                            <Chip
                                                label={agreement.acceptedAt ? 'Accepted' : 'Pending'}
                                                color={agreement.acceptedAt ? 'success' : 'warning'}
                                                size="small"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </Box>

                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Created: {formatDate(agreement.createdAt)}
                                            </Typography>
                                        </Box>

                                        {agreement.acceptedAt && (
                                            <>
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Accepted: {formatDate(agreement.acceptedAt)}
                                                    </Typography>
                                                </Box>
                                                {agreement.acceptor && (
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Accepted by: {agreement.acceptor.fullName || agreement.acceptor.email}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        )}

                                        {isAdmin && !agreement.acceptedAt && (
                                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenModal(agreement);
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(agreement);
                                                    }}
                                                    disabled={loading}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Paper>

            {/* Add/Edit Modal */}
            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', md: 600 },
                        maxHeight: '90vh',
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: 2,
                        p: 4
                    }}
                >
                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                        {editMode ? 'Edit Agreement' : 'Add New Agreement'}
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Affiliate</InputLabel>
                        <Select
                            value={formData.affiliateId}
                            label="Affiliate"
                            onChange={(e) => setFormData({ ...formData, affiliateId: e.target.value })}
                            disabled={editMode}
                        >
                            {affiliates.map((affiliate) => (
                                <MenuItem key={affiliate.id} value={affiliate.id}>
                                    {affiliate.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        multiline
                        rows={15}
                        label="Agreement Content"
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        InputProps={{
                            sx: {
                                height: 'auto',
                                alignItems: 'flex-start'
                            }
                        }}
                        sx={{ mb: 3 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseModal}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                        >
                            {editMode ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* View/Accept Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                    <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Agreement Details
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedAgreement && (
                        <>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Affiliate:
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {selectedAgreement.affiliate.name}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Status:
                                </Typography>
                                <Chip
                                    label={selectedAgreement.acceptedAt ? 'Accepted' : 'Pending'}
                                    color={selectedAgreement.acceptedAt ? 'success' : 'warning'}
                                    size="small"
                                />
                            </Box>

                            {selectedAgreement.acceptedAt && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Accepted on:
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatDate(selectedAgreement.acceptedAt)}
                                    </Typography>
                                    {selectedAgreement.acceptor && (
                                        <Typography variant="body2" color="text.secondary">
                                            by {selectedAgreement.acceptor.fullName || selectedAgreement.acceptor.email}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Box
                                sx={{
                                    whiteSpace: 'pre-line',
                                    backgroundColor: '#f8f9fa',
                                    p: 3,
                                    borderRadius: 1,
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                }}
                            >
                                <Typography variant="body1">
                                    {selectedAgreement.content}
                                </Typography>
                            </Box>

                            {!selectedAgreement.acceptedAt && !isAdmin && (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    mt: 3,
                                    p: 2,
                                    borderRadius: 1,
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: '#f8f9fa'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Checkbox
                                            checked={acceptCheckbox}
                                            onChange={(e) => setAcceptCheckbox(e.target.checked)}
                                        />
                                        <Typography>
                                            I have read and understand Terms and Conditions
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            onClick={openTerms}
                                            size="small"
                                        >
                                            Open Terms
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setViewDialogOpen(false)}
                        color="inherit"
                        variant="outlined"
                    >
                        Close
                    </Button>
                    {selectedAgreement && !selectedAgreement.acceptedAt && !isAdmin && (
                        <Button
                            onClick={handleAcceptAgreement}
                            color="primary"
                            variant="contained"
                            disabled={!acceptCheckbox || loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                        >
                            Accept Agreement
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this agreement? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={loading}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Contract Terms Modal */}
            {termsModalOpen && (
                <ContractTermsModal
                    open={termsModalOpen}
                    onClose={() => setTermsModalOpen(false)}
                    termsId={selectedContractTermsId}
                />
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Agreement;